import imgHoangSaIslands from 'assets/images/EVG/HoangSa.png';
import imgTruongSaIslands from 'assets/images/EVG/TruongSa.png';
import ChargeHelper, { ChargeConstants } from 'features/Charge/chargeHelper';
import MaintainerHelper from 'features/Maintainer/maintainerHelper';
import KTBSDropdown from 'general/components/OtherKeenComponents/KTBSDropdown';
import AppColors from 'general/constants/AppColors';
import AppRoute from 'general/constants/AppRoute';
import UIHelper from 'general/helpers/UIHelper';
import Utils from 'general/utils/Utils';
import useWindowSize from 'hooks/useWindowSize';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { ImageOverlay, MapContainer, Marker, Popup, TileLayer } from 'react-leaflet';
import { useSelector } from 'react-redux';
import StationInfoBoxView from '../StationInfoBoxView';

ChargeStationLeafletMapView.propTypes = {
  iframeMode: PropTypes.bool,
};

const MAP_CENTER = [21.03478655865438, 105.79108899756075];
const MAP_ZOOM = 12;
const HOANG_SA_BOUNDS = [[17.0, 111.5], [18.0, 112.5]];
const TRUONG_SA_BOUNDS = [[11.0, 113.5], [12.0, 114.5]];

const iconCache = {};
function getPinIcon(iconUrl) {
  if (!iconCache[iconUrl]) {
    iconCache[iconUrl] = L.icon({
      iconUrl,
      iconSize: [40, 40],
      iconAnchor: [20, 40],
      popupAnchor: [0, -44],
    });
  }
  return iconCache[iconUrl];
}

const chargingSpeedOptions = [
  { value: '', name: 'Tất cả' },
  { value: 'NORMAL', name: 'Sạc thường: ≤ 11 kW' },
  { value: 'FAST', name: 'Sạc nhanh: 20 kW → 60 kW' },
  { value: 'SUPERFAST', name: 'Sạc siêu nhanh: ≥ 120kW' },
];

function ChargeStationLeafletMapView({ iframeMode = false }) {
  const { user } = useSelector((state) => state.auth);
  const isMaintainer = user?.role === 'MAINTAINER';

  const [filter, setFilter] = useState({ areaId: '', vehicleType: '', type: '', chargingSpeed: '' });
  const [stationList, setStationList] = useState([]);

  useEffect(() => {
    let params = _.pickBy({ ...filter }, (v) => v !== null && v !== undefined && v !== '');
    if (!params.status) {
      params.status = 'ALL';
    }
    const filterDeletedStations = (stations) =>
      (stations ?? []).filter((s) => s.status !== ChargeConstants.ChargeStationStatus.DELETED);

    if (isMaintainer) {
      MaintainerHelper.API.requestGetChargeStationList(params).then((res) => {
        setStationList(filterDeletedStations(res?.chargeStations));
      });
    } else {
      ChargeHelper.API.requestGetChargeStationList(params).then((res) => {
        setStationList(filterDeletedStations(res?.chargeStations));
      });
    }
  }, [filter, isMaintainer]);

  const areaTree = useSelector((state) => state.app.areaTree);
  const areaProvinceList = useMemo(() => ChargeHelper.Utils.getAreaListByType([areaTree], 'P'), [areaTree]);
  const areaProvinceOptions = useMemo(
    () => _.map(areaProvinceList, (item) => ({ name: item.name, value: item.areaId })),
    [areaProvinceList]
  );
  const [selectedAreaProvince, setSelectedAreaProvince] = useState();
  const areaDistrictList = useMemo(() => {
    if (!selectedAreaProvince) return [];
    return ChargeHelper.Utils.getSubAreaListByParentId([areaTree], selectedAreaProvince?.areaId);
  }, [areaTree, selectedAreaProvince]);
  const areaDistrictOptions = useMemo(
    () => _.map(areaDistrictList, (item) => ({ name: item.name, value: item.areaId })),
    [areaDistrictList]
  );
  const [selectedAreaDistrict, setSelectedAreaDistrict] = useState();

  const windowSize = useWindowSize();
  const [contentHeight, setContentHeight] = useState('100vh');
  useEffect(() => {
    const header = document.getElementById('kt_header');
    const headerMobile = document.getElementById('kt_header_mobile');
    const footer = document.getElementById('kt_footer');
    const height =
      windowSize?.height -
      (header?.clientHeight ?? 0) -
      (footer?.clientHeight ?? 0) -
      (headerMobile?.clientHeight ?? 0) -
      1;
    setContentHeight(`${height}px`);
  }, [windowSize]);

  const [isFilterExpanded, setIsFilterExpanded] = useState(true);

  return (
    <div className="position-relative" style={{ height: contentHeight }}>
      <style>{`
        .leaflet-station-popup .leaflet-popup-content-wrapper {
          padding: 0;
          border-radius: 10px;
          overflow: hidden;
          box-shadow: 0 3px 14px rgba(0,0,0,0.4);
        }
        .leaflet-station-popup .leaflet-popup-content {
          margin: 0;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          font-size: 14px;
          text-align: left;
          line-height: 1.5;
        }
        .leaflet-station-popup .leaflet-popup-content span,
        .leaflet-station-popup .leaflet-popup-content div,
        .leaflet-station-popup .leaflet-popup-content p {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          text-align: left;
        }
        .leaflet-station-popup .leaflet-popup-tip-container {
          display: none;
        }
        .leaflet-station-popup .leaflet-popup-close-button {
          display: none;
        }
        .leaflet-station-popup .hover-opacity-80:hover,
        .leaflet-station-popup .hover-opacity-60:hover {
          opacity: 1 !important;
        }
      `}</style>
      <MapContainer
        center={MAP_CENTER}
        zoom={MAP_ZOOM}
        style={{ width: '100%', height: '100%' }}
        zoomControl={false}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        />
        <ImageOverlay url={imgHoangSaIslands} bounds={HOANG_SA_BOUNDS} />
        <ImageOverlay url={imgTruongSaIslands} bounds={TRUONG_SA_BOUNDS} />

        {stationList.map((station) => {
          if (!Utils.isValid(station.latitude) || !Utils.isValid(station.longitude)) return null;
          const iconUrl = ChargeHelper.Utils.getChargeStationPinIcon(station.status);
          return (
            <Marker
              key={station.stationId}
              position={[station.latitude, station.longitude]}
              icon={getPinIcon(iconUrl)}
            >
              <Popup className="leaflet-station-popup" minWidth={300} maxWidth={300}>
                <StationInfoBoxView
                  station={station}
                  onClicked={
                    !iframeMode
                      ? () => {
                          const route = isMaintainer
                            ? `${AppRoute.maintainerStationList}/${station.stationId}`
                            : `${AppRoute.stationList}/${station.stationId}`;
                          Utils.openInNewTab(route);
                        }
                      : undefined
                  }
                />
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>

      {/* FILTER */}
      <div className="position-absolute left-0 top-0 w-100 h-100" style={{ pointerEvents: 'none' }}>
        <div className="h-100 d-flex flex-column-reverse flex-sm-column">
          <div className="row m-0 p-0 px-2 pt-4">
            <div className="col-xl-3 col-lg-4 col-sm-6 mb-4 px-2" style={{ zIndex: 1000 }}>
              {UIHelper.buildCard(
                <div style={{ pointerEvents: 'visible' }}>
                  {UIHelper.buildCardHeader(
                    'Tìm kiếm trạm sạc',
                    false,
                    <button
                      type="button"
                      className="d-flex align-items-center justify-content-center border-0 rounded"
                      style={{
                        width: 35,
                        height: 35,
                        backgroundColor: AppColors.gray[200],
                        cursor: 'pointer',
                        transition: 'transform 0.3s ease',
                        transform: isFilterExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      }}
                      onClick={() => setIsFilterExpanded(!isFilterExpanded)}
                    >
                      <i className="fas fa-chevron-up" style={{ color: AppColors.gray[800] }} />
                    </button>,
                    true
                  )}
                  <div
                    style={{
                      maxHeight: isFilterExpanded ? '1200px' : '0px',
                      opacity: isFilterExpanded ? 1 : 0,
                      overflow: 'hidden',
                      transition: 'all 0.4s ease-in-out',
                    }}
                  >
                    <div className="row m-0 p-0 px-2">
                      <div className="col-12 mb-4 px-2">
                        {UIHelper.buildFormLabel('Tỉnh/Thành phố')}
                        <KTBSDropdown
                          name="province"
                          toggleElement={
                            <button className="btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between" type="button">
                              {selectedAreaProvince?.name ?? 'Chọn tỉnh/thành phố'}
                            </button>
                          }
                          selectedValue={selectedAreaProvince?.areaId}
                          dropdownMenuItems={areaProvinceOptions}
                          dropdownMenuClassName="overflow-auto w-100"
                          dropdownMenuStyle={{ maxHeight: '30vh' }}
                          onChange={(value) => {
                            setSelectedAreaProvince(_.find(areaProvinceList, (item) => item.areaId === value));
                            setFilter({ ...filter, areaId: value });
                          }}
                          isDropdownForm={true}
                          searchable
                          liveSearch={false}
                          onTextChanged={() => {}}
                        />
                      </div>

                      <div className="col-12 mb-4 px-2">
                        {UIHelper.buildFormLabel('Phường/Xã')}
                        <KTBSDropdown
                          name="district"
                          toggleElement={
                            <button className="btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between" type="button">
                              {selectedAreaDistrict?.name ?? 'Chọn phường/xã'}
                            </button>
                          }
                          selectedValue={selectedAreaDistrict?.areaId}
                          dropdownMenuItems={areaDistrictOptions}
                          dropdownMenuClassName="overflow-auto w-100"
                          dropdownMenuStyle={{ maxHeight: '30vh' }}
                          onChange={(value) => {
                            setSelectedAreaDistrict(_.find(areaDistrictList, (item) => item.areaId === value));
                            setFilter({ ...filter, areaId: value });
                          }}
                          isDropdownForm={true}
                          searchable
                          liveSearch={false}
                          onTextChanged={() => {}}
                        />
                      </div>

                      <div className="col-12 mb-4 px-2">
                        {UIHelper.buildFormLabel('Loại phương tiện')}
                        <KTBSDropdown
                          name="vehicleType"
                          toggleElement={
                            <button className="btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between" type="button">
                              {_.find(ChargeHelper.Data.vehicleTypeOptions, (item) => item?.value === filter.vehicleType)?.name ?? 'Tất cả'}
                            </button>
                          }
                          selectedValue={filter.vehicleType}
                          dropdownMenuItems={[{ name: 'Tất cả', value: '' }, ...ChargeHelper.Data.vehicleTypeOptions]}
                          dropdownMenuClassName="overflow-auto w-100"
                          dropdownMenuStyle={{ maxHeight: '30vh' }}
                          onChange={(value) => setFilter({ ...filter, vehicleType: value })}
                          isDropdownForm={true}
                        />
                      </div>

                      <div className="col-12 mb-4 px-2">
                        {UIHelper.buildFormLabel('Loại trụ sạc')}
                        <KTBSDropdown
                          name="type"
                          toggleElement={
                            <button className="btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between" type="button">
                              {_.find(ChargeHelper.Data.chargePointTypeOptions, (item) => item?.value === filter.type)?.name ?? 'Tất cả'}
                            </button>
                          }
                          selectedValue={filter.type}
                          dropdownMenuItems={[{ name: 'Tất cả', value: '' }, ...ChargeHelper.Data.chargePointTypeOptions]}
                          dropdownMenuClassName="overflow-auto w-100"
                          dropdownMenuStyle={{ maxHeight: '30vh' }}
                          onChange={(value) => setFilter({ ...filter, type: value })}
                          isDropdownForm={true}
                        />
                      </div>

                      <div className="col-12 mb-4 px-2">
                        {UIHelper.buildFormLabel('Công suất')}
                        <KTBSDropdown
                          name="chargingSpeed"
                          toggleElement={
                            <button className="btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between" type="button">
                              {_.find(chargingSpeedOptions, (item) => item?.value === filter.chargingSpeed)?.name ?? 'Tất cả'}
                            </button>
                          }
                          selectedValue={filter.chargingSpeed}
                          dropdownMenuItems={chargingSpeedOptions}
                          dropdownMenuClassName="overflow-auto w-100"
                          dropdownMenuStyle={{ maxHeight: '30vh' }}
                          onChange={(value) => setFilter({ ...filter, chargingSpeed: value })}
                          isDropdownForm={true}
                        />
                      </div>
                    </div>

                    <div className="px-4 py-4" style={{ borderTop: `1px dashed ${AppColors.gray[300]}` }}>
                      {UIHelper.buildNegativeButton('Xóa bộ lọc', () => {
                        setSelectedAreaProvince(null);
                        setSelectedAreaDistrict(null);
                        setFilter({ areaId: '', vehicleType: '', type: '', chargingSpeed: '' });
                      })}
                    </div>
                  </div>
                </div>,
                false
              )}
            </div>
            <div className="col"></div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ChargeStationLeafletMapView;
